import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form, Input, Select, Button } from 'antd';
import { createProgram, updateProgramById } from '../../../service/trainingService';
import { getAllEmployeesMethod, getAllDepartments } from '../../../service/ApiServices';

const CreateTrainingProgram = ({ selectedProgram, handleCancel, handleReload }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]); // Ensure initial state is an array
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await getAllDepartments({ search: '', setLoading, setData: setDepartments });
        await getAllEmployeesMethod({
          setEmployeeData: (responseData) => {
            // Match Onboarding's normalization
            console.log('Employee API response:', responseData); // Debug log
            const employeeData = responseData?.data || [];
            if (!Array.isArray(employeeData)) {
              console.error('Employee data is not an array:', employeeData);
              setEmployees([]);
            } else {
              setEmployees(employeeData);
            }
          },
          setLoading,
          search: '',
          params: { page: 1, limit: 1000 }, // Match Onboarding's limit
        });
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load departments or employees');
        setEmployees([]); // Reset to empty array on error
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    console.log('Employees state:', employees); // Debug log
    if (selectedProgram) {
      form.setFieldsValue({
        programName: selectedProgram.programName,
        description: selectedProgram.description,
        duration: selectedProgram.duration,
        type: selectedProgram.type,
        trainer: selectedProgram.trainer?._id,
        department: selectedProgram.department?._id,
        prerequisites: selectedProgram.prerequisites,
      });
    } else {
      form.resetFields();
    }
  }, [selectedProgram, form, employees]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      if (selectedProgram) {
        await updateProgramById(selectedProgram._id, values);
      } else {
        await createProgram(values);
      }
      handleReload();
      handleCancel();
    } catch (error) {
      console.error('Error saving program:', error);
      setError('Failed to save program');
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return <div style={{ color: 'red' }}>{error}</div>;
  }

  return (
    <Form form={form} onFinish={onFinish} layout="vertical">
      <Form.Item
        name="programName"
        label="Program Name"
        rules={[{ required: true, message: 'Please enter program name' }]}
      >
        <Input />
      </Form.Item>
      <Form.Item name="description" label="Description">
        <Input.TextArea />
      </Form.Item>
      <Form.Item
        name="duration"
        label="Duration (hours)"
        rules={[{ required: true, message: 'Please enter duration' }]}
      >
        <Input type="number" />
      </Form.Item>
      <Form.Item
        name="type"
        label="Type"
        rules={[{ required: true, message: 'Please select type' }]}
      >
        <Select>
          <Select.Option value="In-Person">In-Person</Select.Option>
          <Select.Option value="Online">Online</Select.Option>
          <Select.Option value="Hybrid">Hybrid</Select.Option>
        </Select>
      </Form.Item>
      <Form.Item name="trainer" label="Trainer">
        <Select
          allowClear
          showSearch
          filterOption={(input, option) =>
            option?.children?.toLowerCase().includes(input.toLowerCase())
          }
        >
          {Array.isArray(employees) && employees.length > 0 ? (
            employees.map((emp) => (
              <Select.Option key={emp._id} value={emp._id}>
                {emp.full_name}
              </Select.Option>
            ))
          ) : (
            <Select.Option disabled>No trainers available</Select.Option>
          )}
        </Select>
      </Form.Item>
      <Form.Item name="department" label="Department">
        <Select
          allowClear
          showSearch
          filterOption={(input, option) =>
            option?.children?.toLowerCase().includes(input.toLowerCase())
          }
        >
          {Array.isArray(departments) && departments.length > 0 ? (
            departments.map((dept) => (
              <Select.Option key={dept._id} value={dept._id}>
                {dept.departmentName}
              </Select.Option>
            ))
          ) : (
            <Select.Option disabled>No departments available</Select.Option>
          )}
        </Select>
      </Form.Item>
      <Form.Item name="prerequisites" label="Prerequisites" style={{display:"none"}}>
        <Select mode="tags" allowClear />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading}>
          {selectedProgram ? 'Update' : 'Create'} Program
        </Button>
        <Button onClick={handleCancel} style={{ marginLeft: 8 }}>
          Cancel
        </Button>
      </Form.Item>
    </Form>
  );
};

CreateTrainingProgram.propTypes = {
  selectedProgram: PropTypes.shape({
    _id: PropTypes.string,
    programName: PropTypes.string,
    description: PropTypes.string,
    duration: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    type: PropTypes.string,
    trainer: PropTypes.shape({
      _id: PropTypes.string,
    }),
    department: PropTypes.shape({
      _id: PropTypes.string,
    }),
    prerequisites: PropTypes.arrayOf(PropTypes.string),
  }),
  handleCancel: PropTypes.func.isRequired,
  handleReload: PropTypes.func.isRequired,
};

export default CreateTrainingProgram;